/**
 * Full-screen connectivity guide: methodology map + classic strip + “this page” context.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, X } from 'lucide-react';
import { StrategyClarityMap } from './StrategyClarityMap';
import { getStrategyConnectivityHelp } from './strategyConnectivityCopy';
import type { StrategyClassicStripId, StrategyConnectivityContext } from './strategyConnectivityTypes';

const CLASSIC_STRIP: {
  id: StrategyClassicStripId;
  label: string;
  sub: string;
  href: string;
}[] = [
  {
    id: 'strategies',
    label: 'Strategies',
    sub: 'Classic alignment container',
    href: '/prioritization/strategy/list',
  },
  {
    id: 'objectives',
    label: 'Objectives',
    sub: 'Under each strategy',
    href: '/prioritization/strategy/objectives',
  },
  {
    id: 'goals',
    label: 'Goals',
    sub: 'Concrete targets',
    href: '/prioritization/strategy/goals',
  },
  {
    id: 'kernel',
    label: 'Strategy kernel',
    sub: 'Diagnosis · policy · action',
    href: '/prioritization/strategy/kernel',
  },
  {
    id: 'canvas',
    label: 'Strategy canvas',
    sub: 'One-page strategy story',
    href: '/prioritization/strategy/canvas',
  },
  {
    id: 'alignment',
    label: 'Alignment',
    sub: 'Priorities vs objectives',
    href: '/prioritization/strategy/alignment',
  },
  {
    id: 'decisions',
    label: 'Decision history',
    sub: 'Governance memory',
    href: '/prioritization/strategy/decisions',
  },
];

function ClassicStrip({ highlightId }: { highlightId: StrategyClassicStripId | null }) {
  return (
    <div className="mt-5">
      <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-2">
        Classic alignment and related surfaces
      </p>
      <div className="flex flex-wrap gap-2">
        {CLASSIC_STRIP.map((item) => {
          const hi = highlightId === item.id;
          return (
            <Link
              key={item.id}
              to={item.href}
              className={`block  border px-3 py-2 min-w-[8.5rem] max-w-[11rem] transition-colors ${
                hi
                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500/80 dark:bg-[var(--bg-raised)] dark:border-primary-400 dark:ring-primary-400/60'
                  : 'border-[var(--rule)] bg-[var(--bg-raised)]/80 dark:bg-[var(--surface)] hover:border-[var(--rule)] dark:hover:border-[var(--rule)]'
              }`}
            >
              <p className="text-xs font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{item.label}</p>
              <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5 leading-snug">{item.sub}</p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export interface StrategyConnectivityDocModalProps {
  isOpen: boolean;
  onClose: () => void;
  context: StrategyConnectivityContext;
}

export function StrategyConnectivityDocModal({
  isOpen,
  onClose,
  context,
}: StrategyConnectivityDocModalProps) {
  const help = useMemo(() => getStrategyConnectivityHelp(context), [context]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-[var(--rule)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="strategy-connectivity-title"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[var(--rule)] flex-shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <BookOpen className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2
                id="strategy-connectivity-title"
                className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]"
              >
                Strategy and clarity connectivity
              </h2>
              <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                How frameworks tie together and reach prioritization items.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] flex-shrink-0"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="border border-[var(--mustard)] dark:border-[var(--mustard)]/50 bg-[var(--mustard-dim)]/80 dark:bg-[var(--mustard)]/25 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--mustard)] dark:text-[var(--mustard)]/90">
              You are here
            </p>
            <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mt-1">{help.pageTitle}</p>
            <ul className="mt-2 space-y-1.5 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] list-disc list-inside">
              {help.bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-2">
              Full map
            </p>
            <StrategyClarityMap highlightCardId={help.mapHighlightId} showIntro={false} />
          </div>

          <ClassicStrip highlightId={help.classicHighlightId} />

          <div className="border border-[var(--rule)] bg-[var(--surface)]/80 dark:bg-[var(--bg)]/30 px-4 py-3 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            <p className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">Links to prioritization items</p>
            <p>
              From back plan nodes, OST solutions, open questions, desired outcomes, and other clarity surfaces you can create methodology connections to items. On an item, open Classification → Clarity context to see inbound links and traceability.
            </p>
            <p className="mt-2">
              <Link to="/prioritization/items" className="text-primary-600 hover:underline dark:text-primary-400 font-medium">
                Browse items →
              </Link>
            </p>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-[var(--rule)] flex justify-end flex-shrink-0 bg-[var(--surface)]/80 dark:bg-[var(--bg)]/40">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium bg-[var(--bg)] text-white hover:bg-[var(--bg-raised)] dark:hover:bg-[var(--bg-raised)]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export interface StrategyConnectivityHelpButtonProps {
  context: StrategyConnectivityContext;
  className?: string;
  label?: ReactNode;
}

export function StrategyConnectivityHelpButton({
  context,
  className = '',
  label,
}: StrategyConnectivityHelpButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5  border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors ${className}`.trim()}
        title="Opens a guide with the full methodology map (drivers → outcomes, back plans, OST, items, classic alignment)."
      >
        <BookOpen className="h-4 w-4 text-primary-600 flex-shrink-0" />
        {label ?? (
          <>
            <span className="hidden sm:inline">How this connects</span>
            <span className="sm:hidden">Map</span>
          </>
        )}
      </button>
      <StrategyConnectivityDocModal isOpen={open} onClose={() => setOpen(false)} context={context} />
    </>
  );
}
